"use client";
import { StructuredData } from "@/components/common";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  BadgeCheck,
  Check,
  Cpu,
  FileText,
  HardDrive,
  Laptop,
  Network,
  Server,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import ServiceFaq from "@/components/services/ServiceFaq";
import { CTAButton } from "@/components/ui/cta-button";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";

/**
 * /enterprise/dell-technologies-malaysia/ — the Dell partner page.
 *
 * WHO THIS IS FOR: someone searching "Dell partner Malaysia" who needs a
 * quotation or an RFQ answered. They are not browsing; they have a spec or a
 * budget line and want a supplier who will price it in Ringgit. Every section
 * therefore ends within reach of the enquiry form.
 *
 * WHY IT IS A BESPOKE PAGE. This brand graduated from the generic `BrandPage`
 * template (its entry was removed from `brand-pages.ts`) for the same reason
 * Adobe did: enough brand-specific substance to carry its own page. The URL did
 * NOT change — it keeps the crawl history, internal links, llms.txt entry and
 * registered /ms and /zh routes the template page already had.
 *
 * ===================== ACCURACY GATES — READ BEFORE EDITING =====================
 *
 * 1. THE PARTNER CLAIM IS "AUTHORISED DELL RESELLER", NOTHING HIGHER. Anchor
 *    Sprint is an authorised Dell reseller (Jazz, 2026-10-08; it was
 *    "registered in the Dell Technologies Partner Program" from 2026-09-16).
 *    Write "authorised Dell reseller" and nothing stronger — not "official",
 *    not a metal tier, and never a tier badge we have no entitlement to. `brands.ts` gates brand-conferred titles behind
 *    `certified: true`, which is why that flag is now set for Dell. Same
 *    register as the Anthropic wording: state membership, do not embellish it.
 *
 * 2. THE DISTRIBUTOR IS NEVER NAMED. Sourcing wording stays "authorised
 *    distribution" across every surface. This is the sitewide rule and it is
 *    not negotiable on a page a competitor may read.
 *
 * 3. NO PRICES, NO LEAD TIMES, NO STOCK CLAIMS. Configuration and supply both
 *    move. The quotation carries the number and the timeline; the page does
 *    not. "One working day" refers to OUR quotation turnaround, excluding
 *    weekends and public holidays — it is not a delivery promise.
 *
 * 4. The Dell mark is painted as a CSS mask in our own ink, not as Dell's
 *    colour lockup — same treatment as the /enterprise/ directory, and the same
 *    reasoning as the Anthropic badge: describe the relationship in our own
 *    typography rather than dressing the page in someone else's brand.
 */

interface DellPartnerProps {
  locale?: Locale;
}

const stats: Array<{ value: string; label: string }> = [
  { value: "RM", label: "Quoted in Ringgit" },
  { value: "SST", label: "Treatment stated" },
  { value: "MyInvois", label: "E-invoice issued" },
  // Two working days is the sitewide general-quotation language; the one-working-day
  // commitment is tender-specific and stays in the RFQ section, matching the gate on
  // /enterprise/dgx-spark-tender-malaysia/.
  { value: "2", label: "Working days to quote" },
];

/** The "from what to what" spread — one desk through to a rack of AI compute. */
const supplyTiers: Array<{
  icon: LucideIcon;
  title: string;
  detail: string;
  lines: string;
}> = [
  {
    icon: Laptop,
    title: "Workstations & endpoints",
    detail:
      "Precision workstations for CAD, simulation and local model work; OptiPlex desktops and Latitude laptops for the rest of the fleet. Configurations held consistent across a refresh, so fifty seats do not arrive as fifty specifications.",
    lines: "Precision · OptiPlex · Latitude",
  },
  {
    icon: Server,
    title: "Tower & rack servers",
    detail:
      "PowerEdge towers where a business is buying its first real server, and rack models where there is already a cabinet to fill. CPU, memory and drive layout specified against the workload you are actually running rather than a stock SKU.",
    lines: "PowerEdge T-series · R-series",
  },
  {
    icon: Cpu,
    title: "GPU & AI compute",
    detail:
      "GPU-configured PowerEdge for training and inference, and the desk-side GB10 platform where a team needs its own compute rather than a shared queue. We size these against the actual job, because inference and virtualisation size nothing alike.",
    lines: "PowerEdge with GPU · Dell Pro Max with GB10",
  },
  {
    icon: HardDrive,
    title: "Storage & data protection",
    detail:
      "Primary storage and backup targets sized on capacity and on how quickly you would need to be running again, quoted with a support tier that matches that answer rather than the cheapest one available.",
    lines: "PowerStore · PowerVault · PowerProtect",
  },
];

/** What happens after the PO — the half buyers discover they needed. */
const provisioning: Array<{ icon: LucideIcon; title: string; detail: string }> =
  [
    {
      icon: FileText,
      title: "Sized against the workload",
      detail:
        "Send the workload, not a part number. We come back with a configuration and the reasoning for it, including where spending less costs you nothing.",
    },
    {
      icon: Wrench,
      title: "Staged before it ships",
      detail:
        "Firmware and BIOS levels set, iDRAC configured, operating system and drivers staged, so the unit arrives ready to join your estate instead of starting at a setup wizard.",
    },
    {
      icon: Network,
      title: "Rack, power and network planned",
      detail:
        "Rack units, power draw, cooling headroom and switch ports confirmed before delivery — the constraints that turn a clean install into a second site visit.",
    },
    {
      icon: ShieldCheck,
      title: "Warranty and ProSupport set at purchase",
      detail:
        "Support level and multi-year extensions quoted with the hardware, when they cost least, rather than negotiated at the first failure.",
    },
  ];

// FAQ schema co-located here so /ms and /zh render it too (the [locale] catch-all
// renders only the component; a route-file StructuredData never ships there).
const dellFaqs = [
  {
    question: "Is Anchor Sprint an authorised Dell reseller in Malaysia?",
    answer:
      "Yes. Anchor Sprint is an authorised Dell reseller in Malaysia — servers, workstations, storage and GPU compute, quoted in Ringgit with SST treatment stated and a MyInvois e-invoice issued on purchase.",
  },
  {
    question: "What Dell hardware can you quote?",
    answer:
      "PowerEdge tower and rack servers, GPU-configured servers for AI workloads, Precision workstations, OptiPlex desktops, Latitude laptops, and PowerStore, PowerVault and PowerProtect for storage and backup. Send a workload or a seat count and we specify it.",
  },
  {
    question: "Can you size a server for an AI or data workload?",
    answer:
      "Yes, and it is a different exercise from sizing for general virtualisation. We run inference and data pipelines in production, so the memory, interconnect and GPU decisions are made against the job rather than copied from a datasheet.",
  },
  {
    question: "Do you respond to tenders and RFQs?",
    answer:
      "Yes. Send the tender reference and closing date and we return a formal quotation within one working day, with the specification written so an equivalence clause does not disqualify a compliant build. We can also supply the documents a bid pack expects.",
  },
  {
    question: "Do you handle installation, or only supply?",
    answer:
      "Both. Hardware can be staged with firmware, iDRAC and the operating system configured before it ships, with rack, power and network requirements confirmed in advance. Tell us which parts you want to keep in-house.",
  },
];

export default function DellPartner({
  locale = defaultLocale,
}: DellPartnerProps) {
  const prefersReducedMotion = useReducedMotion();
  const lp = (path: string) => getLocalizedPathname(path, locale);
  // Raw path: CTAButton localises its own href (and translates its children),
  // so it is the one place we do NOT pre-localise. lp() is for plain <Link>.
  const quoteHref = "/enterprise/enquiry/?brand=dell-technologies-malaysia";

  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.55, delay, ease: [0.21, 0.6, 0.35, 1] as const },
  });

  const inView = {
    initial: prefersReducedMotion ? false : { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.5, ease: [0.21, 0.6, 0.35, 1] as const },
  };

  return (
    <div className="bg-background">
      <StructuredData type="faq" faqs={dellFaqs} />

      {/* Hero */}
      <section className="bg-background pb-16 pt-20 lg:pt-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
            <div>
              <motion.p
                {...reveal(0)}
                className="mb-5 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted"
              >
                {t(locale, "Partnership")}
              </motion.p>
              <motion.h1
                {...reveal(0.08)}
                className="font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl"
              >
                {t(locale, "Authorised Dell reseller Malaysia")} —{" "}
                <span className="emphasis-underline">
                  {t(locale, "from one workstation to a rack of AI compute")}
                </span>
              </motion.h1>
              <motion.p
                {...reveal(0.16)}
                className="mt-6 max-w-xl text-lg leading-relaxed text-neutral-muted md:text-xl"
              >
                {t(
                  locale,
                  "We specify, quote and provision Dell hardware for Malaysian businesses — servers, workstations, storage and GPU compute — in Ringgit, with SST treatment stated and MyInvois e-invoicing handled.",
                )}
              </motion.p>
              <motion.div
                {...reveal(0.22)}
                className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-muted"
              >
                {[
                  "Quoted in Ringgit",
                  "Tender & RFQ ready",
                  "Provisioned, not just delivered",
                ].map((benefit) => (
                  <span key={benefit} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-primary" aria-hidden />
                    {t(locale, benefit)}
                  </span>
                ))}
              </motion.div>
              <motion.div {...reveal(0.28)} className="mt-8">
                <span className="inline-flex items-center gap-2.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-neutral-emphasis">
                  <BadgeCheck className="h-4 w-4 text-primary" aria-hidden />
                  {t(locale, "Authorised Dell reseller")}
                </span>
              </motion.div>
              <motion.div
                {...reveal(0.34)}
                className="mt-6 flex flex-wrap items-center gap-4"
              >
                <CTAButton
                  href={quoteHref}
                  size="large"
                  className="rounded-full transition"
                >
                  Request a quote
                </CTAButton>
                <CTAButton
                  href="#supply"
                  size="large"
                  variant="outline"
                  className="rounded-full transition"
                >
                  See what we supply
                </CTAButton>
              </motion.div>
            </div>

            {/* Brand mark over the platform we deploy most — framed media.
                The mark is a CSS mask in our own ink, not Dell's colour
                lockup (accuracy gate 4). */}
            <motion.div
              {...reveal(0.2)}
              className="overflow-hidden rounded-2xl border border-border bg-card"
            >
              <div className="flex items-center justify-center px-8 pt-10">
                <span
                  role="img"
                  aria-label={t(locale, "Dell Technologies")}
                  className="block h-12 w-36 bg-neutral-emphasis"
                  style={{
                    maskImage: "url(/images/brands/dell-technologies.svg)",
                    WebkitMaskImage:
                      "url(/images/brands/dell-technologies.svg)",
                    maskSize: "contain",
                    WebkitMaskSize: "contain",
                    maskRepeat: "no-repeat",
                    WebkitMaskRepeat: "no-repeat",
                    maskPosition: "center",
                    WebkitMaskPosition: "center",
                  }}
                />
              </div>
              <div className="mt-8 h-px w-full bg-border" aria-hidden />
              <img
                src="/images/enterprise/dgx-dell-pro-max-gb10-product.jpg"
                alt={t(
                  locale,
                  "Dell Pro Max with GB10 desk-side AI compute platform",
                )}
                className="h-auto w-full object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
        <div className="mt-16 h-px w-full bg-border" aria-hidden />
      </section>

      {/* Stats strip */}
      <section className="bg-background py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            {...inView}
            className="grid grid-cols-2 gap-8 lg:grid-cols-4"
          >
            {stats.map(({ value, label }) => (
              <div key={label}>
                <p className="font-display text-3xl font-bold text-neutral-emphasis md:text-4xl">
                  {value}
                </p>
                <p className="mt-1 text-sm text-neutral-muted">
                  {t(locale, label)}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* What we supply — the from/to spread */}
      <section id="supply" className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="max-w-3xl">
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "One desk, or a rack of them")}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "The same partner covers a single workstation and a multi-node GPU build, which matters when the first purchase is small and the second one is not.",
              )}
            </p>
          </motion.div>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {supplyTiers.map(({ icon: Icon, title, detail, lines }, i) => (
              <motion.div
                key={title}
                {...inView}
                transition={{ ...inView.transition, delay: i * 0.06 }}
                className="rounded-2xl border border-border bg-card p-8"
              >
                <Icon className="h-6 w-6 text-primary" aria-hidden />
                <h3 className="mt-5 font-display text-xl font-semibold text-neutral-emphasis">
                  {t(locale, title)}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-muted">
                  {t(locale, detail)}
                </p>
                <p className="mt-5 text-xs font-medium uppercase tracking-wider text-neutral-muted">
                  {lines}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div {...inView} className="mt-10">
            <Link
              href={lp("/enterprise/dell-pro-max-gb10-malaysia/")}
              className="group inline-flex items-center gap-2 text-sm font-semibold text-primary"
            >
              {t(locale, "See the GB10 desk-side AI platform in detail")}
              <ArrowRight
                className="h-4 w-4 transition-transform group-hover:translate-x-1"
                aria-hidden
              />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Provisioning */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="max-w-3xl">
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "Provisioning, not just supply")}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "Hardware that arrives as a sealed box is half a delivery. These are the steps that decide whether it is running this week or next month.",
              )}
            </p>
          </motion.div>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {provisioning.map(({ icon: Icon, title, detail }, i) => (
              <motion.div
                key={title}
                {...inView}
                transition={{ ...inView.transition, delay: i * 0.06 }}
                className="flex gap-5 rounded-2xl border border-border bg-card p-8"
              >
                <Icon
                  className="h-6 w-6 shrink-0 text-primary"
                  aria-hidden
                />
                <div>
                  <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                    {t(locale, title)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-muted">
                    {t(locale, detail)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tender & RFQ bridge */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            {...inView}
            className="rounded-2xl border border-border bg-card p-10 sm:p-14"
          >
            <div className="max-w-3xl">
              <h2 className="font-display text-2xl font-bold leading-tight text-neutral-emphasis md:text-3xl">
                {t(locale, "Buying through a tender or an RFQ?")}
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-neutral-muted">
                {t(
                  locale,
                  "Send the tender reference and the closing date. We return a formal quotation within one working day, and we will mark where a clause would unintentionally exclude a compliant build.",
                )}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <CTAButton
                  href={quoteHref}
                  size="large"
                  className="rounded-full transition"
                >
                  Send us the specification
                </CTAButton>
                <Link
                  href={lp("/enterprise/dgx-spark-tender-malaysia/")}
                  className="group inline-flex items-center gap-2 text-sm font-semibold text-primary"
                >
                  {t(locale, "How we support tender teams")}
                  <ArrowRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-1"
                    aria-hidden
                  />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="max-w-3xl">
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "Questions buyers actually ask")}
            </h2>
          </motion.div>
          <div className="mt-12">
            <ServiceFaq items={dellFaqs} />
          </div>
        </div>
      </section>

      {/* CTA — dark proof moment */}
      <section className="bg-background pb-24 pt-24 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            {...inView}
            className="rounded-3xl bg-hero-background p-10 sm:p-14"
          >
            <div className="max-w-3xl">
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-primary-contrast/60">
                {t(locale, "Start a conversation")}
              </p>
              <h2 className="font-display text-3xl font-bold leading-tight text-primary-contrast md:text-4xl">
                {t(locale, "Tell us the workload. We will quote the hardware.")}
              </h2>
              <p className="mt-4 text-lg text-primary-contrast/70">
                {t(
                  locale,
                  "A seat count, a workload or a tender document is enough to start. You get a configuration, the reasoning behind it, and a price in Ringgit.",
                )}
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <CTAButton
                  href={quoteHref}
                  size="large"
                  className="rounded-full transition"
                >
                  Request a quote
                </CTAButton>
                <Link
                  href={lp("/enterprise/")}
                  className="group inline-flex items-center justify-center gap-2 rounded-full border border-primary-contrast/20 px-8 py-3 text-sm font-semibold text-primary-contrast transition hover:border-primary-contrast/40 hover:bg-primary-contrast/10"
                >
                  {t(locale, "Browse every brand we supply")}
                  <ArrowRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-1"
                    aria-hidden
                  />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trademark footnote */}
      <section className="bg-background pb-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="max-w-4xl text-xs leading-relaxed text-neutral-muted">
            {t(
              locale,
              "Anchor Sprint is an authorised Dell reseller in Malaysia. Dell, Dell Technologies, PowerEdge, PowerStore, PowerVault, PowerProtect, Precision, OptiPlex, Latitude and ProSupport are trademarks of Dell Inc.; this page does not imply endorsement by Dell.",
            )}
          </p>
        </div>
      </section>
    </div>
  );
}
